import { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, useToast } from '@morressier/alembic';
import { faClockFour } from '@fortawesome/pro-light-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { fetchSubmissionExtensionDeadline } from 'src/api/queries';
import { useRequestSubmissionDeadlineExtension } from 'src/api/commands';
import { IDecisionModalProps } from './decisionModalHelper';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { formatDateSafe } from '../../../utils/date';
import { FormFieldDate } from '../../FormField';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getExtensionSchema } from '@publishing-engine/domain/validation';
import { getMaxUTCDateString } from '@publishing-engine/domain/utils';

export const RequestSubmissionDeadlineExtensionModal = ({
  submissionId,
  submissionContextId,
  onClose
}: IDecisionModalProps & { label?: string }) => {
  const { data } = useQuery({
    queryKey: ['submission-extension-deadline', submissionId],
    queryFn: () => fetchSubmissionExtensionDeadline(submissionId, 'author'),
    enabled: !!submissionId
  });
  const toast = useToast();
  const [isSubmittingLocked, setIsSubmittingLocked] = useState<boolean>(false);

  const { refetchSubmission, workflowResponse, organizationId } =
    useSubmissionData();

  const currentDeadline = formatDateSafe(
    workflowResponse?.workflowData?.currentDeadline ?? ''
  );
  const minDate =
    new Date(currentDeadline) < new Date()
      ? formatDateSafe(new Date())
      : currentDeadline;
  const maxDate = getMaxUTCDateString(currentDeadline);

  const methods = useForm({
    defaultValues: {
      proposedDate: minDate
    },
    resolver: zodResolver(getExtensionSchema(currentDeadline))
  });

  const requestSubmissionDeadlineExtension =
    useRequestSubmissionDeadlineExtension({
      submissionId,
      organizationId,
      submissionContextId: submissionContextId!
    });

  const deadlineExtensions = data?.deadlineExtensions;
  const remainingDeadlineAutoExtension =
    workflowResponse.workflowData.deadlineExtensions?.remainingAutoextensions;

  const handleClose = () => {
    setIsSubmittingLocked(false);
    onClose();
  };

  const onSubmit = async () => {
    if (isSubmittingLocked) {
      return;
    }

    setIsSubmittingLocked(true);

    if (deadlineExtensions?.status === 'requested') {
      toast({
        variant: 'error',
        title: 'Deadline extension already requested.',
        description: 'Please wait until the extension is approved.',
        isClosable: true
      });
      return;
    }

    const response = await requestSubmissionDeadlineExtension.mutateAsync(
      methods.getValues().proposedDate
    );
    let toastTitle = '';
    let toastDescription = '';
    if (remainingDeadlineAutoExtension) {
      toastTitle =
        remainingDeadlineAutoExtension !== 1
          ? 'Extension Granted'
          : 'Extension Requested';
      toastDescription =
        remainingDeadlineAutoExtension !== 1
          ? 'Your request for extension has been automatically granted.'
          : 'Your request was sent to Journal Editors.';
    } else {
      toastTitle = 'Extension Requested';
      toastDescription = 'Your request was sent to this Journal’s Editors.';
    }

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: toastTitle,
        description: toastDescription,
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: 'Error requesting extension',
        description: 'Please try again later.',
        isClosable: true
      });
      setIsSubmittingLocked(false);
    }
    await refetchSubmission();
    handleClose();
  };

  const isSubmitting = requestSubmissionDeadlineExtension?.isPending ?? false;

  return (
    <Modal
      isOpen
      onClose={handleClose}
      width="28rem"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Flex flexDirection="column" gap={8} padding={8}>
            <Flex>
              <FontAwesomeIcon fontSize={32} icon={faClockFour} />
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Request Extension?</Text>
              <Text textStyle="textBody">
                This extension request will be reviewed by the editorial team,
                and you will be notified via email of their decision. If your
                extension request is approved, your deadline will be updated,
                and you will be notified of this change.
              </Text>
              <FormFieldDate
                config={{
                  name: 'proposedDate',
                  component: 'Custom:DateInput',
                  caseDataFields: {
                    main: 'proposedDate'
                  },
                  params: {
                    labels: {
                      main: 'Proposed Deadline Date',
                      subtitle: ''
                    },
                    validations: {
                      kind: 'date',
                      required: true,
                      min: minDate,
                      max: maxDate
                    }
                  }
                }}
              />
            </Flex>
            <Flex justifyContent="flex-end" gap={2}>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || isSubmittingLocked}
              >
                Request Extension
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
